import React from 'react';
import { EmptyState } from './EmptyState';
import { Column } from './TableRow';
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { SectionHeader } from './SectionHeader';
import { TableSection } from './TableSection';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';

type ContributionTableProps = {
  columns: Column[];
  planPrimaryData: any[];
  planSecondaryData: any[];
  planTertiaryData?: any[];
  memberData: any[];
  planPrimaryAddButton: React.ReactNode;
  planSecondaryAddButton: React.ReactNode;
  planTertiaryAddButton?: React.ReactNode;
  memberAddButton: React.ReactNode;
  planTitle: string;
  memberTitle: string;
  planSections: string[];
  memberSections: string[];
  emptyTitle: string;
  emptyPrimarySubtitle: string;
  emptySecondarySubtitle: string;
};

const useStyles = makeStyles()({
  root: {
    marginTop: '0.5rem',
  },
  planColor: {
    backgroundColor: '#559294',
  },
  memberColor: {
    backgroundColor: '#BE8661',
    borderRadius: 0,
    borderTop: 'none',
  },
  tableContainer: {
    border: '1px solid ' + patientAccessTheme.palette.grey[300],
  },
  tableRow: {
    backgroundColor: patientAccessTheme.palette.grey[700],
    height: '36px',
    color: patientAccessTheme.palette.status.white,
  },
  cell: {
    padding: '8px 18px 8px 20px',
    borderRight: '1px solid ' + patientAccessTheme.palette.grey[300],
    borderBottom: '1px solid ' + patientAccessTheme.palette.grey[300],
    '&:last-child': {
      borderRight: 'none',
    },
  },
});

export const ContributionTable: React.FC<ContributionTableProps> = ({
  columns,
  planPrimaryData,
  planSecondaryData,
  planTertiaryData,
  memberData,
  planPrimaryAddButton,
  planSecondaryAddButton,
  planTertiaryAddButton,
  memberAddButton,
  planTitle,
  memberTitle,
  planSections,
  memberSections,
  emptyTitle,
  emptyPrimarySubtitle,
  emptySecondarySubtitle,
}) => {
  const { classes } = useStyles();

  const isTertiary =
    planTertiaryData !== undefined && planTertiaryAddButton !== undefined;

  const primaryEmpty = (
    <EmptyState title={emptyTitle} subtitle={emptyPrimarySubtitle} />
  );
  const secondaryEmpty = (
    <EmptyState title={emptyTitle} subtitle={emptySecondarySubtitle} />
  );

  const SharedTable = ({ children }: { children: React.ReactNode }) => (
    <TableContainer className={classes.tableContainer}>
      <Table>
        <TableHead>
          <TableRow className={classes.tableRow}>
            {columns.map((col) => (
              <TableCell
                className={classes.cell}
                style={{ width: col.width }}
                key={col.accessor}
              >
                <Typography fontSize="14px" fontType="medium" textColor="#fff">
                  {col.name}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{children}</TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Grid className={classes.root}>
      <SectionHeader title={planTitle} colorClass={classes.planColor} />
      <SharedTable>
        <TableSection
          label={planSections[0]}
          addButton={planPrimaryAddButton}
          columns={columns}
          data={planPrimaryData}
          rowType="primary"
          emptyState={primaryEmpty}
        />
        {isTertiary && (
          <TableSection
            label={planSections[1]!}
            addButton={planTertiaryAddButton}
            columns={columns}
            data={planTertiaryData}
            rowType="tertiary"
            emptyState={secondaryEmpty}
          />
        )}
        <TableSection
          label={isTertiary ? planSections[2] : planSections[1]}
          addButton={planSecondaryAddButton}
          columns={columns}
          data={planSecondaryData}
          rowType="secondary"
          emptyState={secondaryEmpty}
        />
      </SharedTable>

      <SectionHeader title={memberTitle} colorClass={classes.memberColor} />
      <SharedTable>
        <TableSection
          label={memberSections[0]}
          addButton={memberAddButton}
          columns={columns}
          data={memberData}
          rowType="member"
          emptyState={primaryEmpty}
        />
      </SharedTable>
    </Grid>
  );
};
